import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../config/api';
import '../styles/BatchCreator.css';

interface Collaboration {
  _id: string;
  handle: string;
  email: string;
  platform: string;
  agreedPrice: number;
  postMetrics?: {
    primaryViews?: number;
    cpm?: number;
  };
  totalViews?: number;
  postUrlTikTok?: string;
  postUrlInstagram?: string;
  postUrlYoutube?: string;
}

interface NegotiationHistory {
  date: string;
  type: 'initial_offer' | 'counter_offer' | 'admin_counter' | 'acceptance' | 'rejection';
  amount: number;
  batchSize: number;
  actor: 'admin' | 'creator';
  totalValue: number;
  message?: string;
}

interface BatchData {
  _id: string;
  handle: string;
  primaryPlatform: string;
  averageCollaborationViews: number;
  lastCollaborationViews: number;
  pricingType?: 'fixed' | 'rpm';
  batchPrice?: number;
  rpmRate?: number;
  maxPrice?: number;
  batchSize: number;
  batchCount: number;
  status: 'pending' | 'accepted' | 'negotiating' | 'rejected';
  negotiatedPrice?: number;
  negotiationHistory?: NegotiationHistory[];
  collaborationId: Collaboration;
  message?: string;
  createdAt: string;
}

const BatchCreator: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const [batch, setBatch] = useState<BatchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNegotiationModal, setShowNegotiationModal] = useState(false);
  const [negotiatedPrice, setNegotiatedPrice] = useState<string>('');
  const [negotiatedPackageSize, setNegotiatedPackageSize] = useState<string>('4');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseStatus, setResponseStatus] = useState<'accepted' | 'negotiating' | 'rejected' | null>(null);

  useEffect(() => {
    fetchBatchProposal();
  }, [batchId]);

  const fetchBatchProposal = async () => {
    try {
      const response = await fetch(`${API_URL}/api/batch/proposal/${batchId}`);
      const result = await response.json();
      
      if (result.success) {
        setBatch(result.data);
        if (result.data.status !== 'pending') {
          setResponseStatus(result.data.status);
        }
      } else {
        setError(result.error || 'Failed to load proposal');
      }
    } catch (err) {
      setError('Failed to load proposal');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/proposal/${batchId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setResponseStatus('accepted');
      } else {
        alert(result.error || 'Failed to accept proposal');
      }
    } catch (err) {
      alert('Failed to accept proposal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    // Set the current batch price/rpmRate as default in negotiation modal
    if (batch) {
      const defaultPrice = batch.pricingType === 'rpm'
        ? (batch.rpmRate || 0).toString()
        : (batch.batchPrice || 0).toString();
      setNegotiatedPrice(defaultPrice);
      setNegotiatedPackageSize(batch.batchSize.toString());
    }
    setShowNegotiationModal(true);
  };

  const handleNegotiationSubmit = async () => {
    if (!negotiatedPrice || parseFloat(negotiatedPrice) < 0) {
      alert('Please enter a valid price');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/proposal/${batchId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          negotiatedPrice: parseFloat(negotiatedPrice),
          negotiatedPackageSize: parseInt(negotiatedPackageSize) || 4
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setResponseStatus('negotiating');
        setShowNegotiationModal(false);
      } else {
        alert(result.error || 'Failed to submit counter offer');
      }
    } catch (err) {
      alert('Failed to submit counter offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJustDecline = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/proposal/${batchId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      const result = await response.json();
      
      if (result.success) {
        setResponseStatus('rejected');
        setShowNegotiationModal(false);
      } else {
        alert(result.error || 'Failed to decline proposal');
      }
    } catch (err) {
      alert('Failed to decline proposal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString('en-US');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getHistoryIcon = (type: string) => {
    switch (type) {
      case 'initial_offer': return '📋';
      case 'counter_offer': return '💰';
      case 'admin_counter': return '🔄';
      case 'acceptance': return '✅';
      case 'rejection': return '❌';
      default: return '📝';
    }
  };

  const getHistoryColor = (actor: string, type: string) => {
    if (type === 'acceptance') return '#10b981';
    if (type === 'rejection') return '#ef4444';
    return actor === 'admin' ? '#3b82f6' : '#f59e0b';
  };

  const renderNegotiationHistory = () => {
    if (!batch?.negotiationHistory || batch.negotiationHistory.length === 0) {
      return null;
    }

    // Don't show history if it's only the initial offer
    if (batch.negotiationHistory.length === 1 && batch.negotiationHistory[0].type === 'initial_offer') {
      return null;
    }

    return (
      <div className="negotiation-history-section" style={{ marginTop: '32px' }}>
        <h3 style={{ color: '#374151', marginBottom: '20px' }}>📈 Negotiation History</h3>
        <div className="timeline" style={{ position: 'relative', paddingLeft: '24px' }}>
          {/* Timeline line */}
          <div style={{
            position: 'absolute',
            left: '12px',
            top: '0',
            bottom: '0',
            width: '2px',
            background: 'linear-gradient(to bottom, #e5e7eb, #d1d5db)',
          }}></div>
          
          {batch.negotiationHistory
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            .map((item, index) => (
            <div key={index} className="timeline-item" style={{
              position: 'relative',
              marginBottom: '24px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              marginLeft: '12px'
            }}>
              {/* Timeline dot */}
              <div style={{
                position: 'absolute',
                left: '-25px',
                top: '20px',
                width: '24px',
                height: '24px',
                background: getHistoryColor(item.actor, item.type),
                borderRadius: '50%',
                border: '3px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                zIndex: 1
              }}>
                {getHistoryIcon(item.type)}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: '600',
                    color: getHistoryColor(item.actor, item.type),
                    marginBottom: '4px',
                    textTransform: 'capitalize'
                  }}>
                    {item.actor === 'admin' ? '🏢 Ssemble' : '👤 You'} - {item.type.replace('_', ' ')}
                  </div>
                  
                  {item.message && (
                    <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 8px 0' }}>
                      {item.message}
                    </p>
                  )}
                  
                  <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: '#374151' }}>
                    {isRpmType ? (
                      <>
                        <span><strong>${item.amount}</strong> RPM rate</span>
                        <span><strong>{item.batchSize}</strong> videos</span>
                      </>
                    ) : (
                      <>
                        <span><strong>${item.amount}</strong> per video</span>
                        <span><strong>{item.batchSize}</strong> videos</span>
                        <span><strong>${item.totalValue.toLocaleString()}</strong> total</span>
                      </>
                    )}
                  </div>
                </div>
                
                <div style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '16px' }}>
                  {formatDate(item.date)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="batch-creator-container">
        <div className="loading">Loading proposal...</div>
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="batch-creator-container">
        <div className="error-message">
          <h2>Proposal Not Found</h2>
          <p>{error || 'This proposal link may be invalid or expired.'}</p>
        </div>
      </div>
    );
  }

  // Show response status if already responded
  if (responseStatus) {
    return (
      <div className="batch-creator-container">
        <div className="response-complete">
          <div className="response-icon">
            {responseStatus === 'accepted' && '✅'}
            {responseStatus === 'negotiating' && '💬'}
            {responseStatus === 'rejected' && '👋'}
          </div>
          <h2>
            {responseStatus === 'accepted' && 'Thank You!'}
            {responseStatus === 'negotiating' && 'Counter Offer Received'}
            {responseStatus === 'rejected' && 'Proposal Declined'}
          </h2>
          <p>
            {responseStatus === 'accepted' && "We'll send you the details soon."}
            {responseStatus === 'negotiating' && "Thank you for your counter offer. We'll review and get back to you."}
            {responseStatus === 'rejected' && 'Thank you for your response. We look forward to future opportunities.'}
          </p>
        </div>
      </div>
    );
  }

  const cpm = batch.collaborationId.postMetrics?.cpm || 0;
  const isRpmType = batch.pricingType === 'rpm';
  const totalPackageValue = isRpmType
    ? ((batch.maxPrice || 0) * batch.batchSize)
    : ((batch.batchPrice || 0) * batch.batchSize);

  return (
    <div className="batch-creator-container">
      <div className="proposal-card">
        {/* Header */}
        <div className="proposal-header">
          <h1>Long-term Collaboration Proposal</h1>
          <p className="subtitle">Thank you for your successful collaboration</p>
        </div>

        {/* Creator Info */}
        <div className="creator-section">
          <h2>{batch.handle}</h2>
          <span className="platform-badge">{batch.primaryPlatform}</span>
        </div>

        {/* Performance Section */}
        <div className="performance-section">
          <h3>Previous Collaboration Performance</h3>
          <div className="metrics-grid">
            <div className="metric-card">
              <span className="metric-label">Last Collaboration Views</span>
              <span className="metric-value">{formatNumber(batch.lastCollaborationViews)}</span>
            </div>
            <div className="metric-card">
              <span className="metric-label">Last Collaboration Post</span>
              <div className="metric-value" style={{ fontSize: '14px' }}>
                {batch.collaborationId.postUrlTikTok && (
                  <a href={batch.collaborationId.postUrlTikTok} target="_blank" rel="noopener noreferrer" 
                     style={{ color: '#3b82f6', textDecoration: 'none', marginRight: '8px' }}>
                    TikTok
                  </a>
                )}
                {batch.collaborationId.postUrlInstagram && (
                  <a href={batch.collaborationId.postUrlInstagram} target="_blank" rel="noopener noreferrer"
                     style={{ color: '#3b82f6', textDecoration: 'none', marginRight: '8px' }}>
                    Instagram
                  </a>
                )}
                {batch.collaborationId.postUrlYoutube && (
                  <a href={batch.collaborationId.postUrlYoutube} target="_blank" rel="noopener noreferrer"
                     style={{ color: '#3b82f6', textDecoration: 'none' }}>
                    YouTube
                  </a>
                )}
                {!batch.collaborationId.postUrlTikTok && !batch.collaborationId.postUrlInstagram && !batch.collaborationId.postUrlYoutube && (
                  <span style={{ color: '#9ca3af', fontSize: '14px' }}>No posts yet</span>
                )}
              </div>
            </div>
            {cpm > 0 && (
              <div className="metric-card">
                <span className="metric-label">CPM</span>
                <span className="metric-value">${cpm.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Proposal Details */}
        <div className="proposal-details">
          <h3>Proposal Details</h3>
          <div className="details-list">
            <div className="detail-row">
              <span className="detail-label">Batch Size</span>
              <span className="detail-value">{batch.batchSize} videos</span>
            </div>
            {isRpmType ? (
              <>
                <div className="detail-row">
                  <span className="detail-label">RPM Rate</span>
                  <span className="detail-value">${batch.rpmRate} / 1,000 views</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Max Price per Video</span>
                  <span className="detail-value">${batch.maxPrice?.toLocaleString()}</span>
                </div>
              </>
            ) : (
              <div className="detail-row">
                <span className="detail-label">Price per Video</span>
                <span className="detail-value">${batch.batchPrice}</span>
              </div>
            )}
            {!isRpmType && (
              <div className="detail-row total">
                <span className="detail-label">Total Package Value</span>
                <span className="detail-value">${totalPackageValue.toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* How RPM Pricing Works */}
          {isRpmType && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: '#ecfdf5',
              borderRadius: '8px',
              borderLeft: '4px solid #10b981'
            }}>
              <h4 style={{
                margin: '0 0 8px 0',
                color: '#059669',
                fontSize: '14px',
                fontWeight: '600'
              }}>💡 How RPM Pricing Works</h4>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                color: '#065f46',
                lineHeight: '1.8',
                fontSize: '14px'
              }}>
                <li>You earn <strong>${batch.rpmRate}</strong> for every 1,000 views</li>
                <li>Payment capped at <strong>${batch.maxPrice?.toLocaleString()}</strong> per video</li>
                <li>Example: 150K views = ${((150 * (batch.rpmRate || 0))).toLocaleString()}</li>
                <li>Better performance = Higher earnings! 🚀</li>
              </ul>
            </div>
          )}

          {/* Message from team if exists */}
          {batch.message && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: '#e0f2fe',
              borderRadius: '8px',
              borderLeft: '4px solid #0284c7'
            }}>
              <h4 style={{
                margin: '0 0 8px 0',
                color: '#0c4a6e',
                fontSize: '14px',
                fontWeight: '600'
              }}>Message from our team:</h4>
              <p style={{
                margin: 0,
                color: '#075985',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.5'
              }}>{batch.message}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button 
            className="btn-accept"
            onClick={handleAccept}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Accept'}
          </button>
          <button 
            className="btn-decline"
            onClick={handleDecline}
            disabled={isSubmitting}
          >
            Decline
          </button>
        </div>
        
        {/* Negotiation History */}
        {renderNegotiationHistory()}
      </div>

      {/* Negotiation Modal */}
      {showNegotiationModal && (
        <div className="modal-overlay" onClick={() => setShowNegotiationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Would you like to make a counter offer?</h3>
            <p>Enter your preferred {isRpmType ? 'RPM rate' : 'price per video'}</p>

            <div className="negotiation-inputs">
              <div className="input-group">
                <label>{isRpmType ? 'RPM Rate (per 1,000 views)' : 'Price per video'}</label>
                <div className="price-input-group">
                  <span className="currency">$</span>
                  <input
                    type="number"
                    value={negotiatedPrice}
                    onChange={(e) => setNegotiatedPrice(e.target.value)}
                    placeholder="0"
                    min="0"
                    step={isRpmType ? "0.5" : "1"}
                  />
                  {isRpmType && <span className="unit">/ 1000 views</span>}
                </div>
              </div>
              <div className="input-group">
                <label>Package size</label>
                <div className="size-input-group">
                  <input
                    type="number"
                    value={negotiatedPackageSize}
                    onChange={(e) => setNegotiatedPackageSize(e.target.value)}
                    placeholder="4"
                    min="1"
                    step="1"
                  />
                  <span className="unit">videos</span>
                </div>
              </div>
            </div>
            
            <div className="modal-buttons">
              <button 
                className="btn-submit-offer"
                onClick={handleNegotiationSubmit}
                disabled={isSubmitting || !negotiatedPrice}
              >
                Submit Counter Offer
              </button>
              <button 
                className="btn-just-decline"
                onClick={handleJustDecline}
                disabled={isSubmitting}
              >
                Just Decline
              </button>
              <button 
                className="btn-cancel"
                onClick={() => setShowNegotiationModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchCreator;